'use strict';

/**
 * @typedef {Object} AnalysisFee
 * @property {number} fee - The fee value
 */

/**
 * @typedef {Object} AnalysisFixedFee
 * @property {{ amount: number }} fee - An object representing money
 */

/**
 * @typedef {Object} AnalysisFees
 * @property {AnalysisFee} administrativeFeeVariable
 * @property {AnalysisFixedFee} administrativeFeeFixed
 * @property {AnalysisFee} ongoingCharge
 * @property {AnalysisFee} netTrailerFee
 * @property {AnalysisFee} managementFee
 * @property {AnalysisFee} managementFeeDiscount
 * @property {AnalysisFee} securitiesAccountFee
 */

/**
 * @typedef {Object} AnalysisReturns
 * @property {number} total
 */

/**
 * @typedef {Object} AnalysisAllocation
 * @property {string} name - Portfolio type name like "Opti 9", etc.
 * @property {AnalysisFees} fees
 * @property {AnalysisReturns} returns
 * @property {Date} fromDate - Date object
 * @property {Date} toDate - Date object
 */

/**
 * calculatePayout - Calculates the total payout
 * This function was derived from @see https://github.com/optise/srv-pension/blob/dev/service/export_analysis.go#L379
 *
 * @param {Object} params - The parameters object
 * @param {number} params.monthsUntilPension - The number of months until pension begins
 * @param {number} params.payoutPeriodInMonths - The number of months over which payouts occur
 * @param {Date} params.currentDate - The starting date
 * @param {Array<AnalysisAllocation>} params.allocations - An object with a getForDate(date) method that returns the allocation data
 * @param {number} params.currentAmount - The starting amount (using native number arithmetic)
 * @returns {number} The total payout calculated
 * @throws {Error} If no allocation is found for the current date
 */
function calculatePayout({
  monthsUntilPension,
  payoutPeriodInMonths,
  currentDate,
  allocations,
  currentAmount,
}) {
  let totalPayout = 0;
  let lastAllocation = null;
  let monthlyReturn = 0;
  let monthlyFees = 0;
  let yearlyFixedFees = 0;

  for (let i = 0; i < monthsUntilPension + payoutPeriodInMonths; i++) {
    currentDate = addMonths(currentDate, 1);

    const nextAllocation = getForDate(currentDate, allocations);

    if (
      nextAllocation &&
      (!lastAllocation || nextAllocation.fromDate !== lastAllocation.fromDate)
    ) {
      lastAllocation = nextAllocation;

      // Calculate monthly return:
      // monthlyReturn = (1 + annualReturn)^(1/12) - 1
      // 1. Add 1 to the annual return to create an annual growth factor.
      // 2. Compute the 12th root (raise to the power of 1/12) of the annual growth factor to determine the equivalent monthly growth factor.
      // 3. Subtract 1 from the monthly growth factor to isolate the effective monthly return.
      monthlyReturn = Math.pow(1 + lastAllocation.returns.total, 1 / 12) - 1;

      // Calculate discount multiplier
      const discountMultiplier =
        1 - lastAllocation.fees.managementFeeDiscount.fee;

      // Calculate monthly fees:
      // monthlyFees = (managementFee * discountMultiplier + ongoingCharge + administrativeFeeVariable - netTrailerFee + 1)^(1/12) - 1
      //  1. Adjust the management fee by the discount multiplier.
      //  2. Add the ongoing charge and the administrative fee variable.
      //  3. Subtract the net trailer fee to account for any fee reductions.
      //  4. Add 1 to form the total annual multiplier (i.e., 1 + annual fee rate).
      //  5. Raise this annual multiplier to the 1/12 power to convert it to a monthly multiplier.
      //  6. Subtract 1 to isolate the effective monthly fee rate.
      monthlyFees =
        Math.pow(
          lastAllocation.fees.managementFee.fee * discountMultiplier +
            lastAllocation.fees.ongoingCharge.fee +
            lastAllocation.fees.administrativeFeeVariable.fee -
            lastAllocation.fees.netTrailerFee.fee +
            1,
          1 / 12,
        ) - 1;

      yearlyFixedFees = lastAllocation.fees.administrativeFeeFixed.fee.amount;
    }

    if (!lastAllocation) {
      throw new Error(
        `No allocation found for date ${currentDate} at month index ${i}`,
      );
    }

    // Calculate return and fees for this month
    const ret = currentAmount * monthlyReturn;
    let fees = currentAmount * monthlyFees;

    // Fixed yearly fees are applied once per year.
    if ((i + 1) % 12 === 0) {
      fees += yearlyFixedFees;
    }

    // Update current amount
    currentAmount = currentAmount + ret - fees;

    // During accumulation period, no payout occurs.
    if (i < monthsUntilPension) {
      continue;
    }

    // Calculate payout
    const payOutMonthsLeft = payoutPeriodInMonths - i + monthsUntilPension;
    const payout = currentAmount / payOutMonthsLeft;
    totalPayout += payout;
    currentAmount -= payout;
  }

  return totalPayout;
}

/**
 * addMonths - Add months to a Date
 * @param {Date} date - The date that we are updating
 * @param {number} months - How much it should be updated
 */
function addMonths(date, months) {
  date.setMonth(date.getMonth() + months);
  return new Date(date);
}

/**
 * getForDate - Searches for and returns the allocation object which date range
 * (fromDate to toDate) includes the given currentDate.
 *
 * @param {Date} currentDate - The current date to search for
 * @param {Array<AnalysisAllocation>} allocationsList - The allocation models to search among
 * @returns {AnalysisAllocation|null} The allocation object if found; otherwise, null.
 */
function getForDate(currentDate, allocationsList) {
  for (const allocation of allocationsList) {
    const from = allocation.fromDate;
    const to = allocation.toDate;

    if (currentDate >= from && currentDate <= to) {
      return allocation;
    }
  }
  return null;
}

/**
 * roundToSignificantDigits - Take given number and round it so that
 * significant number of digits are kept
 * adding magnitude by zeroes
 *
 * @param {number} value - The value to round
 * @param {number} significantDigits - The number of digits rounded to
 * @returns {number} The rounded number, or the value unchanged if too small
 */
function roundToSignificantDigits(value, significantDigits) {
  // If value magnitude does not meet significantDigits' size
  if (Math.pow(10, significantDigits) / 10 > value) {
    return value;
  }

  const valueAsString = String(Math.round(value));
  let significantResultPart = '';

  // Process the value serially
  for (let i = 0; i < significantDigits; i++) {
    significantResultPart += valueAsString[i];
  }

  // Add zeroes
  const insignificantResultPart = Array.from(
    Array(valueAsString.length - significantDigits),
    () => '0',
  ).join('');

  const valueRounded = Number(significantResultPart + insignificantResultPart);
  return valueRounded;
}

/*
    Savings input ranges:
    0.0 -> 0.3: 0 -> 100 000 kr savings, step size 1000
    0.3 -> 0.9: 100 000 -> 1 000 000 kr savings, step size 10 000
    0.9 -> 1.0: 1 000 000 -> 10 000 000 kr savings, step size 100 000
   */
const ranges = {
  LOW: {
    slider: 0.3,
    savings: 100_000,
  },
  MEDIUM: {
    slider: 0.6,
    savings: 900_000,
  },
  HIGH: {
    slider: 0.1,
    savings: 9_000_000,
  },
};
const sliderBoundaries = {
  LOW: ranges.LOW.slider,
  HIGH: 1 - ranges.HIGH.slider,
};

/**
 * calculateRangedSavings - Determine how much
 * savings the calculator slider accounts for
 * within a given range
 *
 * @param {number} progress
 * @param {number} rangeStart - the point where the range start
 * @param {number} rangeSlider - the slider amount the range spans
 * @param {number} rangeSavings - the savings amount the range spans
 */
function calculateRangedSavings(
  progress,
  rangeStart,
  rangeSlider,
  rangeSavings,
) {
  const rangeProgress = (progress - rangeStart) / rangeSlider;
  return rangeProgress * rangeSavings;
}

/**
 * getSavingsFromProgress - Convert a calculator slider input of 0-1
 * to a currency value of 0 to 10M
 *
 * @param {number} progress - of the input slider
 */
function getSavingsFromProgress(progress) {
  let savings = 0;

  if (progress < sliderBoundaries.LOW) {
    // First range, starting at 0
    savings = calculateRangedSavings(
      progress,
      0,
      sliderBoundaries.LOW,
      ranges.LOW.savings,
    );
  } else if (progress < sliderBoundaries.HIGH) {
    // Second range, adding the first range's full savings as an offset
    savings =
      ranges.LOW.savings +
      calculateRangedSavings(
        progress,
        sliderBoundaries.LOW,
        ranges.MEDIUM.slider,
        ranges.MEDIUM.savings,
      );
  } else {
    // Third range, adding both previous ranges' full savings as an offset
    savings =
      ranges.LOW.savings +
      ranges.MEDIUM.savings +
      calculateRangedSavings(
        progress,
        sliderBoundaries.HIGH,
        ranges.HIGH.slider,
        ranges.HIGH.savings,
      );
  }

  // Round to nearest thousand
  return Math.round(savings / 1000) * 1000;
}

exports.calculatePayout = calculatePayout;
exports.roundToSignificantDigits = roundToSignificantDigits;
exports.getSavingsFromProgress = getSavingsFromProgress;
exports.ranges = ranges;
